<template>
  <div :class="{'is-user-profile-view': isUserProfileView}">
    <div class="user-view" v-if="userForUserRole">

      <!-- Non-editing columns -->
      <div v-if="!userRole.isEditing" class="non-editing-container p-d-flex p-ai-center p-jc-start">
        <template v-if="isUserProfileView">
          <!-- User Profile columns -->
          <div id="planName" v-if="!userRole.isEditing" :class="{'is-nested': isNestedPlan}">
            {{ planForUserRole.name }}
          </div>
        </template>
        <template v-else>
          <!-- Plan Settings columns -->
          <div id="avatar">
            <img
              v-if="userForUserRole && userForUserRole.profileImage.fileLocation !== 'null'"
              :src="userForUserRole.profileImage.fileLocation"
              alt=""
            />
            <div class="no-pic" v-else></div>
          </div>
          <div id="firstName">
            {{ userForUserRole.firstName }}
            <template v-if="$store.getters.currentUser.isSuperUser">
              <span class="id-value"> ({{ userForUserRole.id.intID }})</span>
            </template>
          </div>
          <div id="lastName">
            {{ userForUserRole.lastName }}
          </div>
          <div id="email">
            <div class="row-email">
              {{ userForUserRole.emailAddress }}
            </div>
          </div>
        </template>
        <!-- Universal columns -->
        <div id="role">
          <span v-if="userRole.roleId" class="row-role">
            <template v-if="accountOwnerId && accountOwnerId.intID === userForUserRole.id.intID">
              Account Owner <span style="opacity: 0; display: block; height: 0;">(ID {{ userForUserRole.id.intID }})</span>
            </template>
            <template v-else>
              {{ getRoleForId(userRole.roleId)?.title }} <span style="opacity: 0;">(ID {{ userForUserRole.id.intID }})</span>
            </template>
          </span>
        </div>
        <div id="budget" v-if="(isUserProfileView || useBudgets && canUserViewBudgets)">
          <div class="row-budget">
            <!-- TODO: leave change if adding useBudgets flag to GET /accounts/{id} response, hide entire budget column if not -->
            <template v-if="useBudgets">
              <span v-if="userRole.canViewBudget">View</span>
              <span v-else>Hide</span>
            </template>
            <template v-else>
              -
            </template>
          </div>
        </div>
        <div id="editRights">
          <div class="channel-edit-right">
            <div v-if="userRole.canEditAllChannels === false && userRole.channels.length === 0">None</div>
            <div v-else-if="userRole.canEditAllChannels === true || userRole.channels.length >= planForUserRoleChannels.length">
              All
            </div>
            <div v-else>
              <span v-for="(channelId, channelIndex) in userRole.channels" :key="channelIndex">
                  {{`${getChannelForId(channelId)?.name}${channelIndex === userRole.channels.length - 1 ? '' : ', '}`}}
              </span>
            </div>
          </div>
        </div>
        <div id="hiddenTags">
          <template v-if="userHasBlockedTagsForPlan">
            <div class="p-d-flex p-ai-center">
              <span class="pi pi-eye-slash"></span>
            </div>
          </template>
        </div>
        <div v-if="canUserEditPlanUsers && isUserProfileView" id="revokePlanAccess" class="p-d-flex p-ai-center">
          <Button
            class="p-button-text p-button-danger"
            label="Revoke Plan Access"
            @click="(e) => handleDeleteUser(e, userRole)"
          />
          <span 
            v-tooltip.bottom="`Revokes the team member's access to this specific Plan / calendar, but their seat in the account remains. This is to accommodate customers with multiple Plans / calendars in their accounts.`" 
            class="pi pi-question-circle tooltip-color">
          </span>
          <template>
            <Button
              v-if="canUserEditPlanUsers && isUserStatusPending"
              id="resendInvite" 
              label="Resend Invite"
              class="p-button-text"
              style="color: blue;"
              @click="(e) => handleResendInvite(e, userRole.user)"
            />
            <p v-if="isUserStatusPending" style="color: blue; font-weight: 700;">PENDING</p>
          </template>
        </div>
      </div>

      <!-- Editing columns -->
      <div v-if="userRole.isEditing" class="editing-container edit-user">
        <template v-if="isUserProfileView">
          <!-- User Profile columns -->
          <div id="planName" :class="{'is-nested': isNestedPlan}">
            {{ planForUserRole.name }}
          </div>
        </template>
        <template v-else>
          <!-- Plan Settings columns -->
          <div class="edit-user__field" id="firstName">
            <input
              class="p-inputtext"
              type="text"
              v-model="userForUserRole.editingValues.firstName"
              disabled
              autocomplete="off"
              style="border: 0;"
            />
          </div>
          <div class="edit-user__field" id="lastName">
            <input
              class="p-inputtext"
              type="text"
              v-model="userForUserRole.editingValues.lastName"
              disabled
              autocomplete="off"
              style="border: 0;"
            />
          </div>
          <div class="edit-user__field" id="email">
            <div class="row-email">
              <input
                class="p-inputtext"
                type="text"
                v-model="userForUserRole.editingValues.emailAddress"
                disabled
                autocomplete="off"
                style="border: 0;"
              />
            </div>
          </div>
        </template>
        <!-- Universal columns -->
        <div class="edit-user__field" id="role">
          <div class="row-role">
            <Dropdown
              v-model="userRole.editingValues.role"
              :options="roleOptions"
              optionLabel="title"
              placeholder="Select"
              dataKey="key"
              @change="onRoleChange"
            />
          </div>
        </div>
        <div class="edit-user__field" id="budget" v-if="isUserProfileView || (useBudgets && canUserViewBudgets)">
          <div class="row-budget">
            <template v-if="useBudgets">
              <Dropdown
                v-model="userRole.editingValues.canViewBudget"
                :options="budgetOptions"
                optionLabel="name"
                optionValue="code"
                :disabled="
                  userRole.editingValues.role.id &&
                  userRole.editingValues.role.id.intID !== 5 &&
                  userRole.editingValues.role.id.intID !== 6 &&
                  !userRole.editingValues.role.isIntegration
                "
              />
            </template>
            <template v-else>
              -
            </template>
          </div>
        </div>
        <div class="edit-user__field channel-right p-d-flex p-flex-row p-jc-between" id="editRights">
          <MultiSelect
            v-model="userRole.editingValues.channels"
            :options="planForUserRoleChannels"
            optionLabel="name"
            optionValue="id"
            placeholder="Select"
            dataKey="key"
            :disabled="userRole.editingValues.role.id && userRole.editingValues.role.id.intID !== 5 && !userRole.editingValues.role.isIntegration"
            style="flex-grow: 1; max-width: 120px;"
          >
            <template #value="slotProps">
              <template v-if="slotProps.value.length >= planForUserRoleChannels.length">All</template>
              <template v-else-if="slotProps.value.length === 0">None</template>
            </template>
          </MultiSelect>
        </div>
        <div class="edit-user__field" id="hiddenTags">
          <Button
            label="Tags"
            class=""
            icon="pi pi-plus"
            :disabled="!planForUserRole.tags.length"
            @click="() => {
              shouldShowHiddenTags = !shouldShowHiddenTags
            }"
          />
        </div>
        <div v-if="canUserEditPlanUsers && isUserProfileView" id="revokePlanAccess" class="p-d-flex p-ai-center">
          <Button
            class="p-button-text p-button-danger"
            label="Revoke Plan Access"
            @click="(e) => handleDeleteUser(e, userRole)"
          />
          <span 
            v-tooltip.bottom="`Revokes the team member's access to this specific Plan / calendar, but their seat in the account remains. This is to accommodate customers with multiple Plans / calendars in their accounts.`" 
            class="pi pi-question-circle tooltip-color">
          </span>
        </div>

        <div class="edit-user-buttons">
          <div v-if="canUserEditPlanUsers" class="p-d-flex p-ai-center" style="height: 2rem;">
            <!-- Placeholder for height of edit user button -->
          </div>
          <div v-if="canUserEditPlanUsers && !isUserProfileView" class="p-d-flex p-ai-center">
            <Button
              class="p-button-text p-button-danger"
              label="Revoke Plan Access"
              @click="(e) => handleDeleteUser(e, userRole)"
            />
            <span 
              v-tooltip.bottom="`Revokes the team member's access to this specific Plan / calendar, but their seat in the account remains. This is to accommodate customers with multiple Plans / calendars in their accounts.`" 
              class="pi pi-question-circle tooltip-color">
            </span>
          </div>
          <div v-if="canUserEditPlanUsers && !isUserProfileView" class="p-d-flex p-ai-center">
            <Button
              class="p-button-text p-button-danger"
              label="Remove Team Member From Account"
              @click="(e) => handleRemoveUserFromAccount(e, userRole)"
            />
            <span 
              v-tooltip.bottom="`Completely removes the team member from the account and all Plans / calendars they have access to at once. It frees up their seat.`" 
              class="pi pi-question-circle tooltip-color">
            </span>
          </div>
          <Button
            v-if="canUserEditPlanUsers && isUserStatusPending"
            id="resendInvite" 
            label="Resend Invite"
            class="p-button-text"
            style="color: blue;"
            @click="(e) => handleResendInvite(e, userRole.user)"
          />
          <Button
            v-if="!isUserProfileView && canUserEditPlanUsers && !isUserStatusPending"
            label="Reset Password"
            class="p-button-text"
            @click="(e) => handleResetPassword(e, userRole.user)"
          />
          <p v-if="isUserStatusPending" style="color: blue; font-weight: 700; margin: 0.8rem 0 0 0;">PENDING</p>
        </div>
      </div>

      <!-- Editing toggle -->
      <div class="utiliity-container p-d-flex" style="flex-direction: column; gap: 1rem;">
        <template v-if="userRole.isEditing">
          <Button
            v-if="userRole.isEditing"
            class="p-button-text"
            icon="pi pi-check"
            @click="() => handleEditUser(userRole)"
          />
          <Button
            v-if="userRole.isEditing"
            class="p-button-text"
            icon="pi pi-times"
            @click="() => handleCancelEditUser(userRole)"
          />
        </template>
        <template v-else>
          <Button
            v-if="
              canUserEditPlanUsers &&
              accountOwnerId && accountOwnerId.intID !== userForUserRole.id.intID &&
              ($store.getters.currentUser.id.intID !== userForUserRole.id.intID ||
                $store.getters.currentPlanPermissionLevel === accountOwnerRoleLevel)
            "
            class="p-button-text"
            icon="pi pi-pencil"
            @click="() => handleStartEditUser(userRole)"
          />
        </template>
      </div>
    </div>

    <Dialog header="Profile Tags" :footer="`${userForUserRole.firstName} ${userForUserRole.lastName} | ${planForUserRole.name}`" :visible.sync="shouldShowHiddenTags" :modal="true">
      <TagList
        :allowSelect="true"
        :allowEdit="false"
        :allowCreate="false"
        :allowList="true"
        :isBlockedTags="true" 
        :selectedTags="currentBlockedTagsForPlan" 
        :overridePlan="planForUserRole" 
        @updateSelection="onTagListUpdate"
      />
    </Dialog>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ID from '@/models/ID'
import Role from '@/models/Role'
import Channel from '@/models/Channel'
import Plan from '@/models/Plan'
import UserRole from '@/models/UserRole'
import User, { UserStatus } from '@/models/User'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import AccountSeats from '@/models/AccountSeats'
import Tooltip from 'primevue/tooltip'
import Tag from '@/models/Tag'
import Dialog from 'primevue/dialog/Dialog'
import TagList from '@/views/TagList.vue'

export default Vue.extend({
  name: 'PlanSettingsUserDetail',
  props: {
    userRole: {
      type: Object,
      required: true,
    },
    viewType: {
      type: String, 
      required: false,
      default: 'plan-settings',
    }
  },
  directives: {
    tooltip: Tooltip,
  },
  components: {
    Dialog,
    TagList,
  },
  data() {
    return {
      budgetOptions: [
        {
          name: 'Hide',
          code: false,
        },
        {
          name: 'View',
          code: true,
        },
      ],
      shouldShowHiddenTags: false as boolean,
    }
  },
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    planForUserRole(): Plan {
      return this.$store.getters.currentAccount.plans.find((plan) => this.userRole.planId.intID === plan.id.intID)
    },
    isLeadPlan(): boolean {
      return this.planForUserRole.plans.length ? true : false
    },
    isNestedPlan(): boolean {
      return this.planForUserRole.parentId.intID === 0 ? false : true
    },
    planForUserRoleChannels(): Channel[] {
      return this.planForUserRole.channels.filter(
        (channel) => channel.id.intID !== this.currentPlan.initiativeChannel.id.intID && ((this.isLeadPlan && !channel.isNested) || (!this.isLeadPlan && (channel.isLead || channel.isNested)) || (!channel.isLead && !channel.isNested))
      )
    },
    roleOptions(): {}[] {
      return this.$store.getters.allRoles
        .filter((role) => {
          // Integration role is a separate seat pool, only assignable by super admins
          if(role.isIntegration){
            if(!this.$store.getters.currentUser.isSuperUser){
              return false
            }
            // Gate on the integration seat pool
            if(this.currentAccountSeats.isIntegrationLimitReached){
              // Only keep when the edited user already holds an integration seat
              return this.selectedCurrentAccountUserHighestRole?.isIntegration ? true : false
            }
            return true
          }
          if(role.level >= 250){
            // Filter out used roles in per-seat accounts
            if(!this.currentAccountSeats.universal || this.currentAccountSeats.universal.total < 1){
              // Admin and contributor seats
              if(this.currentAccountSeats.adminRemaining < 1 && role.level <= Role.LEVEL_CONTRIBUTOR){
                if(this.selectedCurrentAccountUserHighestRole?.level <= Role.LEVEL_CONTRIBUTOR && !this.selectedCurrentAccountUserHighestRole?.isIntegration){
                  // User has admin/contributor seat
                  return true
                }
                return false
              }
              // Viewer seats
              if(this.currentAccountSeats.viewerRemaining < 1 && role.level === Role.LEVEL_VIEWER){
                if(this.selectedCurrentAccountUserHighestRole?.level === Role.LEVEL_VIEWER){
                  // User has viewer seat
                  return true
                }
                return false
              }
              return true
            }
            return true
          }
          return false
        })
    },
    selectedCurrentAccountUserHighestRole(): Role {
      return this.$store.getters.allRoles.find((role) => role.id.intID === this.userForUserRole.highestRoleId.intID)
    },
    useBudgets(): boolean {
      return this.planForUserRole.useBudgets
    },
    canUserViewBudgets(): boolean {
      // Current logged in user
      return this.$store.getters.currentUserRole.canViewBudget
    },
    userForUserRole(): User {
      return this.$store.getters.allAccountUsers.find(
        (user) => user.id.intID === this.userRole.userId.intID
      )
    },
    roleForUserRole(): Role {
      return this.$store.getters.allRoles.find(
        (role) => role.id.intID == this.userRole.roleId.intID
      )
    },
    accountOwnerRoleLevel(): number {
      return Role.LEVEL_ACCOUNT_OWNER
    },
    canUserEditPlanUsers(): boolean {
      // Current logged in user
      return (
        this.$store.getters.currentPlanPermissionLevel <=
          Role.LEVEL_PLAN_ADMIN ||
        this.$store.getters.currentAccountPermissionLevel ==
          Role.LEVEL_ACCOUNT_OWNER
      )
    },
    accountOwnerId(): ID {
      const ownerUserRole = this.$store.getters.currentAccount.userRoles.find((userRole)=> userRole.roleId.intID == 1)
      return ownerUserRole ? ownerUserRole.userId : new ID()
    },
    isUserStatusPending(): boolean {
      return this.userForUserRole.status === UserStatus.pending
    },
    currentAccountSeats(): AccountSeats {
      return this.$store.getters.currentAccount.seats
    },
    currentBlockedTagsForPlan(): Tag[] {
      return this.planForUserRole.tags.filter((tag) => this.userForUserRole.blockedTagIds.map((blockedId) => blockedId.intID).includes(tag.id.intID))
    },
    userHasBlockedTagsForPlan(): Boolean {
      return this.currentBlockedTagsForPlan.length > 0
    },
    isUserProfileView(): boolean {
      return this.viewType == 'user-profile'
    },
  },
  created: function () {
    this.updateEditingValuesOnUserRole()
  },
  methods: {
    onTagListUpdate(updatedList: Tag[]) {
      // Merge blocked tags
      const tagIdsForPlan = this.planForUserRole.tags.map((tag)=>tag.id)
      const selectedBlockedTagIds = [...updatedList.map((tag) => tag.id)]
      const updatedBlockedTagIds = this.userForUserRole.blockedTagIds.filter((id) => {
        // Exclude removed tag IDs
        if(tagIdsForPlan.filter((planTagId) => planTagId.intID === id.intID).length){
          return false
        }
        return true
      })
      this.userForUserRole.blockedTagIds = [...updatedBlockedTagIds, ...selectedBlockedTagIds]

      // Remove duplicates
      this.userForUserRole.blockedTagIds = this.userForUserRole.blockedTagIds.reduce((accumulator:ID[], currentValue: ID) => {
        if (accumulator.filter((tagId) => tagId.intID === currentValue.intID).length === 0) {
            accumulator.push(currentValue)
        }
        return accumulator
      }, [])
    },
    updateEditingValuesOnUserRole() {
      this.userRole.editingValues.role = this.roleForUserRole
      this.userRole.editingValues.user = this.userForUserRole
      this.userRole.editingValues.channels = this.userRole.channels
      this.userRole.editingValues.canViewBudget = this.userRole.canViewBudget
      this.userForUserRole.editingValues.blockedTagIds = this.userForUserRole.blockedTagIds
    },
    getRoleForId(roleId: ID) {
      return this.$store.getters.allRoles.find(
        (role) => role.id.intID == roleId.intID
      )
    },
    getChannelForId(channelId: ID): Channel | undefined {
      return this.planForUserRoleChannels.find(
        (channel) => channel.id.apiID === channelId.apiID
      )
    },
    handleStartEditUser(userRole: UserRole) {
      // Re-sync editing values from the current persisted state before entering
      // edit mode. The list re-renders by index after refreshes (see :key="index"
      // in PlanSettingsUsers.vue), so the one-time `created` snapshot can be stale,
      // which left the role dropdown unpopulated when editing.
      this.updateEditingValuesOnUserRole()
      userRole.isEditing = true
    },
    handleCancelEditUser(userRole: UserRole) {
      userRole.resetFromEditing(this.planForUserRoleChannels)
      this.userForUserRole.resetBlockedTagIds()
      userRole.isEditing = false
    },
    async handleEditUser(userRole: UserRole) {
      userRole.isEditing = false

      const editedUserRole = userRole.transformForEditing()
      editedUserRole.planId = this.planForUserRole.id
      editedUserRole.accountId = this.$store.getters.currentAccount.id
      editedUserRole.roleId = this.roleForUserRole.id
      editedUserRole.userId = this.userForUserRole.id

      if(userRole.roleId.intID === 4 || ((userRole.roleId.intID === 5 || userRole.editingValues.role.isIntegration) && userRole.channels.length >= this.planForUserRoleChannels.length)){
        editedUserRole.canEditAllChannels = true
      }else{
        editedUserRole.canEditAllChannels = false
      }

      // Persist changes (leaving async setup for the single UserRole, in case it can be used elsewhere)
      const userRolesToUpdate = [editedUserRole] as UserRole[]
      try {
        // Sequential calls using async/await and a for loop
        for (let i = 0; i < userRolesToUpdate.length; i++) {
          await this.$store.state.services.users.updateUserRole(userRolesToUpdate[i])
        }
        // Update User to persist changes to blockedTagIds
        await this.$store.state.services.users.update(this.userForUserRole)
        // After all promises resolve...
        await this.$store.dispatch('refreshCurrentAccount').then(
          ()=>{
            if(this.viewType === 'plan-settings'){
              // TODO: call a new action to refresh this.planForUserRole instead of AppStore.currentPlan?
              this.$store.dispatch('refreshCurrentPlan')
            }
            this.updateEditingValuesOnUserRole()
          }
        )
      } catch (error: any) {
        console.warn(error);
        if (error.message.includes('status code 422') || error.message.includes('status code 409')) {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: 'This email address is connected to a user who already has a Seat in this Account. If the user is not listed as having access to this Plan, give them access by clicking on the Select From Account drop down.',
          });
        } else {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: 'Error editing user.',
            detal: error,
          });
        }
      }
    },
    handleDeleteUser(e, userRole: UserRole) {
      // e.stopPropagation()
      this.$confirm.require({
        acceptLabel: 'Revoke Plan Access',
        rejectLabel: 'Cancel',
        message: `Are you sure you want to revoke the team member's access to this Plan?`,
        acceptClass: 'btn-delete-accept',
        target: e.currentTarget,
        accept: () => {
          this.userRole.isEditing = false
          this.$store.state.services.users.deleteUserRole(userRole.id.intID).then(() => {
            this.planForUserRole.userRoles = this.planForUserRole.userRoles.filter(
              (planUserRole) => planUserRole.id.intID !== userRole.id.intID
            )

            // Update account and plan to refresh account seats
            this.$store.dispatch('refreshCurrentAccount').then(
              ()=>{
                // TODO: call a new action to refresh this.planForUserRole instead of AppStore.currentPlan?
                this.$store.dispatch('refreshCurrentPlan', {openedChannels: [], planToUse: this.planForUserRole}) 
              }
            )
          })
        },
        reject: () => {
          //callback to execute when user rejects the action
        },
      })
    },
    handleRemoveUserFromAccount(e, userRole: UserRole){
      // e.stopPropagation()
      this.$confirm.require({
        acceptLabel: 'Remove From Account',
        rejectLabel: 'Cancel',
        message: `Removing this individual from the account will revoke their login credentials and access to all Plans. They can only be added back to the Account and given access to Plans by an Admin. Click to confirm.`,
        target: e.currentTarget,
        acceptClass: 'btn-delete-accept',
        accept: async () => {
          // TODO: test code for cleaning up user roles (unsure when this was written as of 03.05.2025 - JV)
          // const userRolesForUserInAccount =
          //   this.$store.getters.currentAccount.userRoles.filter(
          //     (userRole) => userRole.userId.intID == this.user.id.intID
          //   )
          // await Promise.all(
          //   userRolesForUserInAccount.map(async (userRole) => {
          //     await this.$store.state.services.users.deleteUserRole(
          //       userRole.id.intID
          //     )
          //   })
          // )
          
          this.$store.state.services.users.deleteUserFromAccount(this.userForUserRole.id.intID, this.$store.getters.currentAccount.id.intID).then(
            (response) => {
              // Update account and plan to refresh account seats
              this.$store.dispatch('refreshCurrentAccount').then(
                ()=>{
                  // TODO: call a new action to refresh this plan instead of AppStore.currentPlan?
                  this.$store.dispatch('refreshCurrentPlan', {openedChannels: [], planToUse: this.planForUserRole}) 
                }
              )

              Vue.prototype.$toast.add({
                severity: AlertMessageSeverity.success,
                summary: 'User removed from account.',
                life: 3000,
              })

              this.$router.back()
            },
            (error) => {
              Vue.prototype.$toast.add({
                severity: AlertMessageSeverity.error,
                summary: 'Error',
                detail: 'User could not be removed from account.',
              })
            }
          )
        },
        reject: () => {
          //callback to execute when user rejects the action
        },
      })
    },
    handleResendInvite(e, user: User) {
      e.stopPropagation()
      if (!user.resendInvite) {
        user.resendInvite = true
        this.$store.getters.services.users.resendInvite(user.id.intID).then(
        ()=>{
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.success,
            summary: 'Invite resent.',
          })
        }, 
        (error)=>{
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: `Error resending invitation. ${error}`,
          })
        }
      )
      }
    },
    handleResetPassword(e, user: User) {
      e.stopPropagation()
      this.$store.getters.services.users.forgotPassword(this.userForUserRole.emailAddress).then(
        ()=>{
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.success,
            summary: 'Password reset email sent.',
            life: 3000,
          })
        }, 
        (error)=>{
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: `Error sending password reset email. ${error}`,
          })
        }
      )
    },
    onRoleChange(event) {
      // Default to allow budget view
      this.userRole.editingValues.canViewBudget = true

      // Integration role mirrors Contributor defaults (all channels)
      if(event?.value?.isIntegration){
        this.userRole.editingValues.channels = this.planForUserRoleChannels.map(
          (channel) => channel.id
        )
        return
      }

      switch (event?.value?.id?.intID) {
        case 1:
        case 2:
        case 3:
        case 4:
        case 5:
          // Account-level (1-3), Admin (4), or Contributor (5) role selected
          this.userRole.editingValues.channels = this.planForUserRoleChannels.map(
            (channel) => channel.id
          )
          break
        case 6:
          // Viewer role selected
          this.userRole.editingValues.channels = []
          break
        default:
          // Do nothing
          break
      }
    },
  },
})
</script>